import { Input, cn } from '@recipes/ui';
import { Check, Eye, EyeOff, X } from 'lucide-react';
import { forwardRef, useId, useState, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { passwordRequirements, passwordStrength } from './password-strength';

export interface PasswordFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value'
> {
  /** Current value (controlled through react-hook-form's watch) for the strength meter. */
  value?: string;
  /** Show strength meter and rule checklist (registration / new password). */
  showStrength?: boolean;
  invalid?: boolean;
}

const STRENGTH_COLORS = ['bg-muted', 'bg-destructive', 'bg-warning', 'bg-info', 'bg-success'];

/** Password input with show/hide toggle and optional strength hint. */
export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  ({ value = '', showStrength = false, className, invalid, ...props }, ref) => {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const meterId = useId();
    const strength = passwordStrength(value);
    const rules = passwordRequirements(value);
    const describedBy = [props['aria-describedby'], showStrength ? meterId : null]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={cn('space-y-2', className)}>
        <div className="relative">
          <Input
            ref={ref}
            type={visible ? 'text' : 'password'}
            autoComplete={props.autoComplete ?? 'current-password'}
            {...props}
            invalid={invalid}
            aria-describedby={describedBy || undefined}
            className="pr-11"
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 inline-flex w-11 items-center justify-center text-muted-foreground hover:text-foreground"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? t('auth.hidePassword') : t('auth.showPassword')}
            aria-pressed={visible}
          >
            {visible ? (
              <EyeOff className="size-4" aria-hidden />
            ) : (
              <Eye className="size-4" aria-hidden />
            )}
          </button>
        </div>
        {showStrength && (
          <div id={meterId} className="space-y-1.5">
            <div className="flex gap-1" aria-hidden>
              {[1, 2, 3, 4].map((level) => (
                <span
                  key={level}
                  className={cn(
                    'h-1.5 flex-1 rounded-full',
                    level <= strength ? STRENGTH_COLORS[strength] : 'bg-muted',
                  )}
                />
              ))}
            </div>
            <p className="text-xs text-muted-foreground" data-testid="password-strength">
              {t('auth.strength.label')}: {t(`auth.strength.${strength}`)}
            </p>
            <ul className="space-y-0.5 text-xs">
              {(['length', 'letters', 'numbers'] as const).map((rule) => (
                <li
                  key={rule}
                  className={cn(
                    'flex items-center gap-1',
                    rules[rule] ? 'text-success' : 'text-muted-foreground',
                  )}
                >
                  {rules[rule] ? (
                    <Check className="size-3.5" aria-hidden />
                  ) : (
                    <X className="size-3.5" aria-hidden />
                  )}
                  <span>
                    {t(`auth.rules.${rule}`)}
                    <span className="sr-only">
                      : {rules[rule] ? t('common.yes') : t('common.no')}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  },
);
PasswordField.displayName = 'PasswordField';
